import React, { FC, useMemo } from 'react';
import { Trans, TFunction } from 'next-i18next';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import Sticker from 'src/common/components/Sticker';
import { StickerConfig } from 'src/common/components/Sticker/stickerMap';
import Typography, { type Size } from 'src/common/components/Typography';
import styles from './StickersHeader.module.scss';

export type StickersHeaderProps = {
  /**
   * If true, the stickers will wiggle on hover and change on click
   */
  isInteractive?: boolean;
  /**
   * If true, the stickers for small, medium, and large will hide and show per breakpoint
   */
  isStickerPositionBasedOnBreakpoint?: boolean;
  stickerAction?: StickerConfig;
  stickerBoat?: StickerConfig;
  stickerThing?: StickerConfig;
  /**
   * This refers to the key set in the corresponding translation file
   */
  i18nKey: string;
  size?: Size;
  sizeFromBreakpointM?: Size;
  /**
   * This is the corresponding translation file that contains the i18nKey
   */
  translationFunction?: TFunction;
};

const StickersHeader: FC<StickersHeaderProps> = ({
  i18nKey,
  isInteractive = true,
  isStickerPositionBasedOnBreakpoint = false,
  size = 'display-3',
  sizeFromBreakpointM = 'display-1',
  stickerAction,
  stickerBoat,
  stickerThing,
  translationFunction,
}) => (
  <div className={getClassNames(styles, 'root', classNames({ isStickerPositionBasedOnBreakpoint }))}>
    <div className={styles.titleWrapper}>
      <Typography
        type="h2"
        size={size}
        sizeFromBreakpointM={sizeFromBreakpointM}
        className={styles.title}
      >
        <Trans
          t={translationFunction}
          i18nKey={i18nKey}
          components={useMemo(() => (
            {
              ...(stickerBoat && {
                boatSticker: (
                  <div>
                    <Sticker sticker={stickerBoat} height={92} type="inline" withShadow isInteractive={isInteractive} loading="eager" />
                  </div>
                ),
              }),
              ...(stickerAction && {
                actionSticker: (
                  <div>
                    <Sticker sticker={stickerAction} height={92} type="inline" withShadow isInteractive={isInteractive} loading="eager" />
                  </div>
                ),
              }),
              ...(stickerThing && {
                thingSticker: (
                  <div className={styles.stickerDefault}>
                    <Sticker sticker={stickerThing} height={92} type="inline" withShadow isInteractive={isInteractive} loading="eager" />
                  </div>
                ),
              }),
              ...(stickerThing && isStickerPositionBasedOnBreakpoint && {
                thingStickerMed: (
                  <div className={styles.stickerMedium}>
                    <Sticker sticker={stickerThing} height={92} type="inline" withShadow isInteractive={isInteractive} loading="eager" />
                  </div>
                ),
              }),
              ...(stickerThing && isStickerPositionBasedOnBreakpoint && {
                thingStickerLarge: (
                  <div className={styles.stickerLargeUp}>
                    <Sticker sticker={stickerThing} height={92} type="inline" withShadow isInteractive={isInteractive} loading="eager" />
                  </div>
                ),
              }),
              break: (
                <div className={styles.break} />
              ),
              breakSmall: (
                <div className={styles.breakSmall} />
              ),
              breakMedium: (
                <div className={styles.breakMedium} />
              ),
              breakLarge: (
                <div className={styles.breakLarge} />
              ),
            }
          ), [
            isInteractive,
            isStickerPositionBasedOnBreakpoint,
            stickerAction,
            stickerBoat,
            stickerThing,
          ])}
        />
      </Typography>
    </div>
  </div>
);

export default StickersHeader;
